import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const defaultLinks = [
  { label: 'الرئيسية', path: '/' },
  { label: 'من نحن', path: '/about' },
  { label: 'خدماتنا', path: '/services' },
  { label: 'أعمالنا', path: '/our-work' },
  { label: 'المدونة', path: '/blogs' },
  { label: 'تواصل معنا', path: '/contact' },
];

const NavbarSettings = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [links, setLinks] = useState(defaultLinks);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    const fetchNavbar = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/navbar`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { settings, links: serverLinks } = res.data;
        if (settings) {
          setPhoneNumber(settings.phoneNumber || '');
          setLogoUrl(settings.logoUrl || '');
        }
        if (serverLinks && serverLinks.length > 0) {
          setLinks(
            serverLinks.map((l) => ({
              id: l.id,
              label: l.label,
              path: l.path,
              sortOrder: l.sortOrder,
              isActive: l.isActive ?? 1,
            }))
          );
        }
      } catch (err) {
        // ignore for now, will use defaults
      } finally {
        setLoading(false);
      }
    };

    fetchNavbar();
  }, []);

  const handleLinkChange = (index, field, value) => {
    setLinks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddLink = () => {
    setLinks((prev) => [...prev, { label: '', path: '' }]);
  };

  const handleRemoveLink = (index) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const moveLink = (index, direction) => {
    setLinks((prev) => {
      const next = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      const temp = next[index];
      next[index] = next[newIndex];
      next[newIndex] = temp;
      return next;
    });
  };

  const handleLogoFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data?.url) {
        setLogoUrl(res.data.url);
        setSuccess('تم رفع صورة الشعار بنجاح.');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'فشل رفع الصورة. تأكد من أن الملف صورة أقل من 5MB.';
      setError(message);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        phoneNumber,
        logoUrl,
        links: links.map((l, index) => ({
          label: l.label,
          path: l.path,
          sortOrder: index + 1,
          isActive: true,
        })),
      };

      await axios.put(`${API_BASE_URL}/api/admin/navbar`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess('تم حفظ إعدادات الشريط العلوي بنجاح.');
    } catch (err) {
      const message = err.response?.data?.message || 'حدث خطأ أثناء الحفظ.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>جاري تحميل إعدادات الشريط العلوي...</p>;
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">إعدادات الشريط العلوي (Navbar)</h2>
      <p className="admin-section-subtitle">
        يمكنك هنا تعديل رقم التواصل، صورة الشعار، وترتيب الروابط في الشريط العلوي للموقع.
      </p>

      {error && <div className="admin-section-error">{error}</div>}
      {success && <div className="admin-section-success">{success}</div>}

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>رقم التواصل (واتساب)</label>
          <input
            type="text"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+9665xxxxxxxx"
          />
          <small>سيتم استخدام هذا الرقم في زر "تواصل معنا".</small>
        </div>

        <div className="admin-field">
          <label>رابط صورة الشعار (Logo URL)</label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="مثال: /uploads/logo.png أو رابط خارجي"
          />
          <small>يمكنك رفع الشعار إلى الخادم أو استخدام رابط صورة خارجي.</small>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
            />
          </div>
        </div>
      </div>

      <div className="admin-links-section">
        <div className="admin-links-header">
          <h3>روابط الشريط العلوي</h3>
          <button type="button" className="admin-btn-secondary" onClick={handleAddLink}>
            إضافة رابط جديد
          </button>
        </div>

        <div className="admin-links-list">
          {links.map((link, index) => (
            <div key={index} className="admin-link-row">
              <div className="admin-link-controls">
                <button
                  type="button"
                  className="admin-btn-icon"
                  onClick={() => moveLink(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="admin-btn-icon"
                  onClick={() => moveLink(index, 1)}
                >
                  ↓
                </button>
              </div>
              <div className="admin-link-fields">
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
                  placeholder="عنوان الرابط (مثال: الرئيسية)"
                />
                <input
                  type="text"
                  value={link.path}
                  onChange={(e) => handleLinkChange(index, 'path', e.target.value)}
                  placeholder="المسار (مثال: /about)"
                />
              </div>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={() => handleRemoveLink(index)}
              >
                حذف
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </button>
      </div>
    </div>
  );
};

export default NavbarSettings;

